import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'
import { DateTime } from 'luxon'
import { newId } from '@/utils'

const getDefaultState = () => {
  return {
    data: [],
    ref: ''
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return { ...state.data }
  },
  ref(state) {
    return state.ref
  },
  waste(state) {
    return Object.assign({}, ...state.data.map((i) => ({ [i.id]: i })))
  },
  wasteById(state) {
    return (id) => state.data.find((w) => w.id === id) || null
  },
  wasteByStoreId(state, getters) {
    return (storeId) => {
      return Object.values(getters.waste).filter(
        (w) => w.storeId === storeId || w.storeId === '' || w.storeId === null
      )
    }
  },
  isWaste(state, getters) {
    return getters.waste && Object.values(getters.waste).length > 0
  },
  approvedWaste(state, getters) {
    return getters.wasteOrdered
      .filter((w) => w.status === 'approved')
      .map((w) => w.id)
  },
  pendingWasteCount(state, getters) {
    return getters.pendingWaste.length || 0
  },
  wasteByStatus: (state, getters) => (status) => {
    return getters.wasteOrdered
      .filter((w) => w.status === status)
      .map((w) => w.id)
  },
  pendingWaste(state, getters) {
    return getters.wasteByStatus('pending')
  },
  completedWaste(state, getters) {
    return getters.wasteByStatus('approved')
  },
  wasteOrdered(state, getters) {
    return Object.values(getters.waste).sort((a, b) => {
      const dateA = (a.createdAt && a.createdAt.toDate()) || new Date(0)
      const dateB = (b.createdAt && b.createdAt.toDate()) || new Date(0)
      return dateB - dateA
    })
  },
  pendingWasteByStoreId(state, getters) {
    return (storeId) => {
      const pendingWaste = getters.pendingWaste.map((id) =>
        getters.wasteById(id)
      )
      return pendingWaste.filter((w) => w.storeId === storeId)
    }
  },
  pendingWasteCountByStoreId(state, getters) {
    return (storeId) => {
      return getters.pendingWasteByStoreId(storeId).length || 0
    }
  }
}

const actions = {
  bindStore: firestoreAction(({ bindFirestoreRef, commit }, { storeId }) => {
    let ref = db.collection('stores').doc(storeId).collection('waste')
    commit('setRef', ref)
    return bindFirestoreRef(
      'data',
      ref.where(
        'createdAt',
        '>=',
        DateTime.now().minus({ days: 30 }).toJSDate()
      )
    )
  }),

  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),

  async createWaste({ getters, rootGetters, dispatch }, data) {
    const id = newId()
    const now = Timestamp.now()

    const wasteData = {
      id,
      transactionType: 'waste',
      status: 'pending',
      date: data.date || now,

      itemType: data.itemType,
      itemId: data.itemId,
      itemName: data.itemName,

      quantity: data.quantity,
      unitType: data.unitType,
      unitCost: data.unitCost || 0,
      totalCost: data.totalCost || 0,

      reason: data.reason || '',
      notes: data.notes || '',

      sourceStoreId: rootGetters['storeModule/id'],
      sourceStoreName: rootGetters['storeModule/name'],

      createdAt: now,
      createdBy: rootGetters['user/id']
    }

    return getters.ref.doc(id).set(wasteData)
  },

  async updateWaste({ getters, rootGetters, dispatch }, { id, ...data }) {
    const updateData = {
      ...data,
      transactionType: 'waste',
      updatedAt: Timestamp.now().toDate(),
      updatedBy: rootGetters['user/id']
    }
    return dispatch('updateItem', { id, data: updateData })
  },

  async deleteWaste({ getters, rootGetters }, { id }) {
    console.log('Deleting waste log:', id)
    const result = await getters.ref.doc(id).set(
      {
        status: 'deleted',
        deletedAt: Timestamp.now().toDate(),
        deletedBy: rootGetters['user/id']
      },
      { merge: true }
    )
    console.log('Waste log deleted:', id)
    return result
  },

  async approveWaste({ getters, rootGetters, dispatch }, { id }) {
    console.log('Approving waste log:', id)
    const waste = getters.wasteById(id)
    if (!waste) {
      console.error('Could not find waste log:', id)
      return null
    }

    const result = await dispatch('updateItem', {
      id,
      data: {
        status: 'approved',
        approvedAt: Timestamp.now().toDate(),
        approvedBy: rootGetters['user/id']
      }
    })
    console.log('Waste log approved:', id)
    return result
  },

  updateItem({ getters }, { id, data }) {
    return getters.ref.doc(id).set(data, { merge: true })
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
